(() => {
  function initGallery(gallery) {
    if (!gallery || gallery.dataset.productGalleryInitialized === 'true') return;

    const mainMediaWrapper = gallery.querySelector('[data-product-main-media-wrapper]');
    const thumbnails = Array.from(gallery.querySelectorAll('[data-product-thumbnail]'));
    const mediaTemplates = Array.from(gallery.querySelectorAll('[data-product-media-template]'));

    const lightbox = gallery.querySelector('[data-product-lightbox]');
    const lightboxMediaDisplay = gallery.querySelector('[data-product-lightbox-media-display]');
    const lightboxMediaTemplates = Array.from(
      gallery.querySelectorAll('[data-product-lightbox-media-template]')
    );
    const lightboxCounter = gallery.querySelector('[data-product-lightbox-counter]');
    const lightboxClose = gallery.querySelector('[data-product-lightbox-close]');
    const lightboxPrev = gallery.querySelector('[data-product-lightbox-prev]');
    const lightboxNext = gallery.querySelector('[data-product-lightbox-next]');
    const lightboxImages = Array.from(
      gallery.querySelectorAll('[data-product-lightbox-image]')
    ).map((image) => ({
      mediaId: image.dataset.mediaId || '',
      imageId: image.dataset.imageId || '',
      mediaType: image.dataset.mediaType || 'image',
      src: image.dataset.imageSrc || '',
      srcset: image.dataset.imageSrcset || '',
      alt: image.dataset.imageAlt || ''
    }));

    if (!mainMediaWrapper) return;

    gallery.dataset.productGalleryInitialized = 'true';

    let selectedThumbnail =
      gallery.querySelector('.product-image-thumbnail--active') || thumbnails[0] || null;

    let selectedMediaId =
      selectedThumbnail?.dataset.mediaId ||
      mainMediaWrapper.querySelector('[data-product-main-media]')?.dataset.mediaId ||
      '';

    let selectedImageId =
      selectedThumbnail?.dataset.imageId ||
      mainMediaWrapper.querySelector('[data-product-lightbox-trigger]')?.dataset.imageId ||
      lightboxImages[0]?.imageId ||
      '';

    let autoplayTimer = null;
    let lightboxIndex = 0;
    let lastFocusedElement = null;

    const autoplayEnabled = gallery.dataset.galleryAutoplay === 'true';
    const autoplayInterval = Number(gallery.dataset.galleryAutoplayInterval) || 5000;

    const getTemplateByMediaId = (mediaId) => {
      if (!mediaId) return null;

      return mediaTemplates.find((template) => template.dataset.mediaId === String(mediaId)) || null;
    };

    const getLightboxTemplateByMediaId = (mediaId) => {
      if (!mediaId) return null;

      return (
        lightboxMediaTemplates.find((template) => template.dataset.mediaId === String(mediaId)) ||
        null
      );
    };

    const pauseLightboxMedia = () => {
      lightboxMediaDisplay?.querySelectorAll('video').forEach((video) => {
        if (typeof video.pause === 'function') {
          video.pause();
        }
      });
    };

    const getThumbnailByMediaId = (mediaId) => {
      if (!mediaId) return null;

      return thumbnails.find((thumbnail) => thumbnail.dataset.mediaId === String(mediaId)) || null;
    };

    const getThumbnailByImageId = (imageId) => {
      if (!imageId) return null;

      return thumbnails.find((thumbnail) => thumbnail.dataset.imageId === String(imageId)) || null;
    };

    const getLightboxIndexByMediaId = (mediaId) => {
      if (!mediaId) return -1;

      return lightboxImages.findIndex((image) => image.mediaId === String(mediaId));
    };

    const getLightboxIndexByImageId = (imageId) => {
      if (!imageId) return -1;

      return lightboxImages.findIndex((image) => image.imageId === String(imageId));
    };

    const pauseCurrentMedia = () => {
      mainMediaWrapper.querySelectorAll('video').forEach((video) => {
        if (typeof video.pause === 'function') {
          video.pause();
        }
      });
    };

    const renderMainMedia = (thumbnail) => {
      if (!thumbnail) return;

      const mediaId = thumbnail.dataset.mediaId || '';
      const currentMedia = mainMediaWrapper.querySelector('[data-product-main-media]');
      const currentMediaId = currentMedia?.dataset.mediaId || '';
      const template = getTemplateByMediaId(mediaId);

      if (!template || currentMediaId === mediaId) return;

      pauseCurrentMedia();

      mainMediaWrapper.replaceChildren(template.content.cloneNode(true));

      const lightboxTrigger = mainMediaWrapper.querySelector('[data-product-lightbox-trigger]');

      if (lightboxTrigger) {
        lightboxTrigger.dataset.mediaId = mediaId;
        lightboxTrigger.dataset.imageId = thumbnail.dataset.imageId || '';
      }
    };

    const selectThumbnail = (thumbnail) => {
      if (!thumbnail) return;

      selectedThumbnail = thumbnail;
      selectedMediaId = thumbnail.dataset.mediaId || selectedMediaId;
      selectedImageId = thumbnail.dataset.imageId || selectedImageId;

      thumbnails.forEach((item) => {
        item.classList.remove('product-image-thumbnail--active');
        item.setAttribute('aria-current', 'false');
      });

      thumbnail.classList.add('product-image-thumbnail--active');
      thumbnail.setAttribute('aria-current', 'true');

      renderMainMedia(thumbnail);
    };

    const selectByMediaId = (mediaId) => {
      if (!mediaId) return false;

      const thumbnail = getThumbnailByMediaId(mediaId);

      if (!thumbnail) return false;

      selectThumbnail(thumbnail);
      return true;
    };

    const selectByImageId = (imageId) => {
      if (!imageId) return false;

      const thumbnail = getThumbnailByImageId(imageId);

      if (!thumbnail) return false;

      selectThumbnail(thumbnail);
      return true;
    };

    const stopAutoplay = () => {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const startAutoplay = () => {
      if (!autoplayEnabled || thumbnails.length < 2 || lightbox?.open) return;

      stopAutoplay();

      autoplayTimer = setInterval(() => {
        const currentIndex = thumbnails.indexOf(selectedThumbnail);
        const nextIndex = currentIndex >= thumbnails.length - 1 ? 0 : currentIndex + 1;

        selectThumbnail(thumbnails[nextIndex]);
      }, autoplayInterval);
    };

    const updateLightboxImage = (index) => {
      if (!lightbox || !lightboxMediaDisplay || lightboxImages.length === 0) return;

      const mediaCount = lightboxImages.length;
      const normalizedIndex = ((index % mediaCount) + mediaCount) % mediaCount;
      const media = lightboxImages[normalizedIndex];
      const template = getLightboxTemplateByMediaId(media.mediaId);

      lightboxIndex = normalizedIndex;

      if (template) {
        pauseLightboxMedia();
        lightboxMediaDisplay.replaceChildren(template.content.cloneNode(true));
      }

      if (lightboxCounter) {
        lightboxCounter.textContent = `Media ${normalizedIndex + 1} of ${mediaCount}`;
      }
    };

    const openLightbox = (trigger) => {
      if (!lightbox || !lightboxMediaDisplay || lightboxImages.length === 0) return;

      stopAutoplay();

      lastFocusedElement = document.activeElement;

      const requestedMediaId = trigger?.dataset.mediaId || selectedMediaId;
      const requestedImageId = trigger?.dataset.imageId || selectedImageId;
      const requestedMediaIndex = getLightboxIndexByMediaId(requestedMediaId);
      const requestedImageIndex = getLightboxIndexByImageId(requestedImageId);

      updateLightboxImage(
        requestedMediaIndex >= 0
          ? requestedMediaIndex
          : requestedImageIndex >= 0
            ? requestedImageIndex
            : 0
      );

      if (typeof lightbox.showModal === 'function') {
        lightbox.showModal();
      } else {
        lightbox.setAttribute('open', '');
      }

      lightboxClose?.focus({ preventScroll: true });
    };

    const closeLightbox = () => {
      if (!lightbox) return;

      pauseLightboxMedia();
      
      if (typeof lightbox.close === 'function' && lightbox.open) {
        lightbox.close();
      } else {
        lightbox.removeAttribute('open');
        startAutoplay();

        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
          lastFocusedElement.focus({ preventScroll: true });
        }
      }
    };

    const showPreviousLightboxImage = () => {
      if (lightboxImages.length < 2) return;

      updateLightboxImage(lightboxIndex - 1);
    };

    const showNextLightboxImage = () => {
      if (lightboxImages.length < 2) return;

      updateLightboxImage(lightboxIndex + 1);
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('mouseenter', () => {
        if (thumbnail.dataset.mediaType !== 'image') return;

        stopAutoplay();
        renderMainMedia(thumbnail);
      });

      thumbnail.addEventListener('mouseleave', () => {
        if (thumbnail.dataset.mediaType !== 'image') return;

        renderMainMedia(selectedThumbnail);
        startAutoplay();
      });

      thumbnail.addEventListener('focus', () => {
        if (thumbnail.dataset.mediaType !== 'image') return;

        stopAutoplay();
        renderMainMedia(thumbnail);
      });

      thumbnail.addEventListener('blur', () => {
        if (thumbnail.dataset.mediaType !== 'image') return;

        renderMainMedia(selectedThumbnail);
        startAutoplay();
      });
    });

    gallery.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('[data-product-thumbnail]');

      if (thumbnail && gallery.contains(thumbnail)) {
        stopAutoplay();
        selectThumbnail(thumbnail);
        return;
      }

      const lightboxTrigger = event.target.closest('[data-product-lightbox-trigger]');

      if (lightboxTrigger && gallery.contains(lightboxTrigger)) {
        openLightbox(lightboxTrigger);
      }
    });

    lightboxClose?.addEventListener('click', closeLightbox);
    lightboxPrev?.addEventListener('click', showPreviousLightboxImage);
    lightboxNext?.addEventListener('click', showNextLightboxImage);

    lightbox?.addEventListener('click', (event) => {
      if (event.target === lightbox) {
        closeLightbox();
      }
    });

    lightbox?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPreviousLightboxImage();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNextLightboxImage();
      }
    });

    lightbox?.addEventListener('close', () => {
      startAutoplay();

      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus({ preventScroll: true });
      }
    });

    gallery.addEventListener('product-gallery:select-image', (event) => {
      const mediaId = event.detail?.mediaId;
      const imageId = event.detail?.imageId;

      if (!mediaId && !imageId) return;

      stopAutoplay();

      if (mediaId) {
        selectByMediaId(String(mediaId));
      } else if (imageId) {
        selectByImageId(String(imageId));
      }

      startAutoplay();
    });

    gallery.addEventListener('product-gallery:select-media', (event) => {
      const mediaId = event.detail?.mediaId;

      if (!mediaId) return;

      stopAutoplay();
      selectByMediaId(String(mediaId));
      startAutoplay();
    });

    if (selectedThumbnail) {
      selectThumbnail(selectedThumbnail);
    }

    startAutoplay();
  }

  function initProductGalleries(container = document) {
    container.querySelectorAll('[data-product-gallery]').forEach(initGallery);
  }

  document.addEventListener('product:variant-change', (event) => {
    const variant = event.detail?.variant;

    if (!variant) return;

    const mediaId = variant.featured_media_id || variant.featured_media?.id;
    const imageId = variant.featured_image_id;

    if (!mediaId && !imageId) return;

    const root =
      event.detail?.root?.closest?.('.product-showcase') ||
      event.detail?.root?.closest?.('.product') ||
      event.detail?.root?.closest?.('.product-card');

    if (!root) return;

    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      gallery.dispatchEvent(
        new CustomEvent('product-gallery:select-image', {
          bubbles: true,
          detail: {
            mediaId: mediaId ? String(mediaId) : '',
            imageId: imageId ? String(imageId) : '',
            variant
          }
        })
      );
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    initProductGalleries();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initProductGalleries(event.target);
  });

  window.GLBProductGallery = {
    init: initProductGalleries
  };
})();